import React, { useCallback, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material//Delete';
import EditIcon from '@mui/icons-material//Edit';
import { Box, Chip, Typography } from '@mui/material';
import { IndividualQuestionBaseRepresentation, RequestedFile } from 'api';
import { QUESTION_TYPE_CHOICES } from 'common/constants/firms';
import { useStore } from 'common/store';
import { Option } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { ExtraActionsMenu, FormDialog } from 'components';
import CustomPrimaryButton from 'components/CustomPrimaryButton/CustomPrimaryButton';
import { SingleItem } from 'components/ExtraActionsMenu/types';
import { getPageReferenceLabel, getReferenceTypeStyle } from 'pages/Firms/utilities/pageReferenceUtilities';
import { PageReferenceType } from 'pages/Firms/utilities/types';
import { sortQuestionsById } from 'pages/ProcessManagement/utils/sortQuestions';
import { DocsAndQuestionsFormValues } from 'services/hooks/useRequestDocsAndQuestions/types';
import useRequestDocsAndQuestions from 'services/hooks/useRequestDocsAndQuestions/useRequestDocsAndQuestions';
import theme from 'theme';
import { StyledDeleteButton } from './RequestContentListOfTasks.style';
import { RequestContentListOfTasksProps } from './types';
import AddItemInput from '../AddItemInput/AddItemInput';
import PlusItemButton from '../PlusItemButton/PlusItemButton';
import AddOrUpdateTaskDialog from './components/AddOrUpdateTaskDialog';

// This type guard function checks if the task object is of type RequestedFile.
// This is necessary because IndividualQuestionBaseRepresentation does not have the reference_object_type property.
const isRequestedFile = (task: RequestedFile | IndividualQuestionBaseRepresentation): task is RequestedFile =>
  (task as RequestedFile).reference_object_type !== undefined;


const RequestContentListOfTasks = ({
  isDocumentRequest,
  tasks,
  companyId,
  setUpdateTaskList,
  isInitialSetup,
  primaryInformationRequestId,
  currentPageData,
  isCompanyTemplate,
}: RequestContentListOfTasksProps) => {
  const [openAddOrUpdateTaskDialog, setOpenAddOrUpdateTaskDialog] = useState(false);
  const [isMaxCharactersError, setIsMaxCharactersError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<RequestedFile | IndividualQuestionBaseRepresentation | null>(null);
  const [{ firmInfo }] = useStore() as unknown as UseStoreValues;
  const { id: firmId } = firmInfo || {};

  let formInitialValues: DocsAndQuestionsFormValues = {
    id: 0,
    name: '',
    question_type: QUESTION_TYPE_CHOICES[0].value,
    children_questions: [],
    options: [] as Option[],
    company_information_request_status: undefined,
    folder: undefined,
    reference_object_id: null,
    reference_object_type: null,
  };

  if (!isDocumentRequest) {
    formInitialValues = {
      ...formInitialValues,
      children_questions: [],
      options: [],
      question_type: QUESTION_TYPE_CHOICES[0].value,
    };
  }
  const [formValues, setFormValues] = useState(formInitialValues);
  const [addOrUpdateTaskDialogFormValues, setAddOrUpdateTaskDialogFormValues] = useState(formValues);

  const { handleRequestNewItem, handleDeleteRequestedItem } = useRequestDocsAndQuestions({
    formInitialValues,
    formValues,
    isDocumentRequest,
    firmId,
    companyId,
    taskToDelete,
    setUpdateTaskList,
    setOpenDeleteDialog,
    setOpen: setOpenAddOrUpdateTaskDialog,
    setIsEdit,
    setFormValues,
    isInitialSetup,
    primaryInformationRequestId,
    currentPageData,
  });

  const editTask = useCallback(
    task => {
      const updatedTask = { ...task };

      if (!isDocumentRequest) {
        const questionTypeChoice = QUESTION_TYPE_CHOICES.find(choice => choice.label === task.question_type);
        updatedTask.question_type = questionTypeChoice ? questionTypeChoice.value : task.question_type;
      }

      setAddOrUpdateTaskDialogFormValues(updatedTask);
      setIsEdit(true);
      setOpenAddOrUpdateTaskDialog(true);
    },
    [isDocumentRequest]
  );

  const deleteTask = useCallback(task => {
    setOpenDeleteDialog(true);
    setTaskToDelete(task);
  }, []);

  const options = useMemo(
    () => [
      {
        label: 'Edit',
        callback: editTask,
        isActive: true,
        icon: <EditIcon />,
      },
      {
        label: 'Delete',
        callback: deleteTask,
        isActive: true,
        icon: <DeleteIcon />,
      },
    ],
    [deleteTask, editTask]
  );

  const displayTasks = useMemo(
    () =>
      isDocumentRequest
        ? tasks
        : sortQuestionsById(tasks as IndividualQuestionBaseRepresentation[]),
    [isDocumentRequest, tasks]
  );

  return (
    <>
      <Box margin="2rem 0">
        <form onSubmit={handleRequestNewItem}>
          <AddItemInput
            isDocumentRequest={isDocumentRequest}
            formValues={formValues}
            setFormValues={setFormValues}
            setIsMaxCharactersError={setIsMaxCharactersError}
          />
          {displayTasks.map(task => (
            <Box
              key={task.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: '4px',
                padding: '0.5rem',
                margin: '0.5rem 0',
              }}>
              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  color: theme.palette.black,
                  marginRight: 'auto',
                }}>
                {task.name}
              </Typography>
              {isDocumentRequest && isRequestedFile(task) && task.reference_object_type && (
                <Chip
                  label={getPageReferenceLabel(task.reference_object_type as PageReferenceType)}
                  style={getReferenceTypeStyle(task.reference_object_type as PageReferenceType)}
                  variant="outlined"
                />
              )}
              <ExtraActionsMenu options={options} dotsColor={theme.palette.grey[300]} singleItem={task as SingleItem} />
            </Box>
          ))}
          <PlusItemButton
            isDocumentRequest={isDocumentRequest}
            setOpenAddItemModal={setOpenAddOrUpdateTaskDialog}
            isMaxCharactersError={isMaxCharactersError}
            formValues={formValues}
          />
        </form>
      </Box>
      <AddOrUpdateTaskDialog
        isDocumentRequest={isDocumentRequest}
        firmId={firmId}
        companyId={companyId ?? null}
        setUpdateTaskList={setUpdateTaskList}
        isInitialSetup={isInitialSetup}
        primaryInformationRequestId={primaryInformationRequestId}
        currentPageData={currentPageData}
        openAddOrUpdateTaskDialog={openAddOrUpdateTaskDialog}
        setOpenAddOrUpdateTaskDialog={setOpenAddOrUpdateTaskDialog}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        isMaxCharactersError={isMaxCharactersError}
        setIsMaxCharactersError={setIsMaxCharactersError}
        formInitialValues={formInitialValues}
        addOrUpdateTaskDialogFormValues={addOrUpdateTaskDialogFormValues}
        setAddOrUpdateTaskDialogFormValues={setAddOrUpdateTaskDialogFormValues}
        isCompanyTemplate={isCompanyTemplate}
      />
      <FormDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title={`DELETE ${isDocumentRequest ? 'DOCUMENT REQUEST' : 'QUESTION'}`}
        showSaveButton={false}
        customCancelButton={
          <CustomPrimaryButton onClick={() => setOpenDeleteDialog(false)} variant="outlined">
            Cancel
          </CustomPrimaryButton>
        }
        customButton={
          <StyledDeleteButton onClick={handleDeleteRequestedItem} variant="contained">
            Delete
          </StyledDeleteButton>
        }>
        <Typography>
          {`${isDocumentRequest ? 'Document request' : 'Question'} to delete:`} <strong>{taskToDelete?.name}</strong>
        </Typography>
      </FormDialog>
    </>
  );
};

export default RequestContentListOfTasks;
